import { zodResolver } from '@hookform/resolvers/zod';
import { FileSearch } from 'lucide-react';
import { useState } from 'react';
import { type Control, type UseFormRegisterReturn, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { type z } from 'zod';

import { mensagemDeErro } from '@/lib/erros';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Dialog } from '@/ui/primitives/dialog';
import { Campo, Input } from '@/ui/primitives/input';
import {
  type CartaoCnpj,
  type CriarEmpresaRequest,
  criarEmpresaRequestSchema,
  cnpjSchema,
} from '@fiscoai/contracts';

import { useCriarEmpresa } from './api';
import { completarComCartao, mensagemDeConsulta, useCartaoCnpj } from './api-consultas';
import { ResumoCartao } from './resumo-cartao';

type Entrada = z.input<typeof criarEmpresaRequestSchema>;

/** Dados gerais que o cartão completa no pedido de criação (os que a pessoa não digitou). */
function comCartao(dados: CriarEmpresaRequest, cartao: CartaoCnpj | null): CriarEmpresaRequest {
  if (!cartao) return dados;
  return {
    ...dados,
    nomeFantasia: dados.nomeFantasia ?? cartao.nomeFantasia,
    inicioAtividades: dados.inicioAtividades ?? cartao.inicioAtividades,
    telefone: dados.telefone ?? cartao.telefone,
    crt: dados.crt ?? cartao.crtSugerido,
    endereco: dados.endereco ?? cartao.endereco,
  };
}

/** CNPJ com a ação de buscar na Receita (habilitada só com CNPJ válido). */
function CampoCnpj(p: {
  control: Control<Entrada>;
  registro: UseFormRegisterReturn<'cnpj'>;
  erro: string | undefined;
  buscando: boolean;
  aoBuscar: () => void;
}) {
  const valido = cnpjSchema.safeParse(useWatch({ control: p.control, name: 'cnpj' })).success;
  return (
    <Campo rotulo="CNPJ" erro={p.erro}>
      {(a) => (
        <div className="flex gap-2">
          <Input
            {...a}
            {...p.registro}
            className="font-mono uppercase"
            placeholder="00.000.000/0000-00"
          />
          <Button
            variante="secundario"
            disabled={!valido}
            carregando={p.buscando}
            onClick={p.aoBuscar}
          >
            <FileSearch aria-hidden />
            Buscar dados na Receita
          </Button>
        </div>
      )}
    </Campo>
  );
}

/** Só o essencial para criar; o cartão CNPJ preenche o resto e o cadastro se completa nas abas. */
export function DialogoNovaEmpresa({
  aberto,
  aoMudar,
}: {
  aberto: boolean;
  aoMudar: (aberto: boolean) => void;
}) {
  const navegar = useNavigate();
  const criar = useCriarEmpresa();
  const consulta = useCartaoCnpj();
  const [usarRegime, setUsarRegime] = useState(false);
  const form = useForm<Entrada, unknown, CriarEmpresaRequest>({
    resolver: zodResolver(criarEmpresaRequestSchema),
    defaultValues: { cnpj: '', razaoSocial: '' },
  });
  const erros = form.formState.errors;
  const cartao = consulta.data ?? null;

  const buscar = () => {
    const cnpj = cnpjSchema.safeParse(form.getValues('cnpj'));
    if (!cnpj.success) return;
    consulta.mutate(cnpj.data, {
      onSuccess: (dados) => {
        form.setValue('razaoSocial', dados.razaoSocial, { shouldValidate: true });
      },
    });
  };

  const fechar = () => {
    aoMudar(false);
    form.reset();
    consulta.reset();
    setUsarRegime(false);
  };

  const enviar = form.handleSubmit((dados) => {
    criar.mutate(comCartao(dados, cartao), {
      onSuccess: (empresa) => {
        void concluir(empresa.id);
      },
      onError: (erro) => toast.error(mensagemDeErro(erro)),
    });
  });

  const concluir = async (id: string) => {
    if (cartao) {
      await completarComCartao(id, {
        cnaes: cartao.cnaes,
        inscricoes: cartao.inscricoesEstaduais,
        regimes: usarRegime ? cartao.regimesSugeridos : [],
      }).catch((erro: unknown) => toast.error(mensagemDeErro(erro)));
    }
    toast.success('Empresa cadastrada', {
      description: cartao
        ? 'Dados da Receita aplicados. Confira o regime e complete o contador.'
        : 'Complete regime, inscrições e contador.',
    });
    fechar();
    void navegar(`/empresas/${id}`);
  };

  return (
    <Dialog
      aberto={aberto}
      aoMudar={(abrir) => {
        if (abrir) aoMudar(true);
        else fechar();
      }}
      titulo="Nova empresa"
      descricao="Informe o CNPJ (numérico ou alfanumérico) e busque os dados na Receita, ou preencha à mão."
      acoes={
        <>
          <Button variante="fantasma" onClick={fechar}>
            Cancelar
          </Button>
          <Button variante="primario" onClick={() => void enviar()} carregando={criar.isPending}>
            Cadastrar empresa
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        <CampoCnpj
          control={form.control}
          registro={form.register('cnpj')}
          erro={
            erros.cnpj?.message ??
            (consulta.isError ? mensagemDeConsulta(consulta.error, 'CNPJ') : undefined)
          }
          buscando={consulta.isPending}
          aoBuscar={buscar}
        />
        <Campo rotulo="Razão social" erro={erros.razaoSocial?.message}>
          {(a) => <Input {...a} {...form.register('razaoSocial')} />}
        </Campo>
        {cartao && (
          <ResumoCartao cartao={cartao} usarRegime={usarRegime} aoMudarUsarRegime={setUsarRegime} />
        )}
      </form>
    </Dialog>
  );
}
